import * as React from 'react'

import { action, makeObservable, observable } from 'mobx'
import { inject, observer } from 'mobx-react'
import { classList } from 'react-classlist-helper'

import Localization from '~/client/src/shared/localization/LocalizationManager'
import { getFullscreenElement } from '~/client/src/shared/utils/fullscreenElement'

import GlobeView from '../../models/GlobeView'
import Sitemap from '../../models/Sitemap'
import GlobeViewsStore from '../../stores/domain/GlobeViews.store'
import LocationAttributesStore from '../../stores/domain/LocationAttributes.store'
import SitemapsStore from '../../stores/domain/Sitemaps.store'
import EventsStore from '../../stores/EventStore/Events.store'
import EventTypes from '../../stores/EventStore/eventTypes'
import { DEFAULT_NAME } from '../../stores/GlobeViewControl.store'
import MapBoxViewerStore from '../MapBoxViewer/MapBoxViewer.store'
import {
  buildMapViewItems,
  buildMapViewSubtitle,
  MapViewsPicker,
  type MapViewSource,
} from '../MapViews/MapViewsPicker'
import { MapViewsSidebar } from '../MapViews/MapViewsSidebar/MapViewsSidebar'
import { IMapViewItem } from '../MapViews/MapViewsSidebar/MapViewsSidebarListItem'
import { PopoverTrigger } from '../shadcn-ui/popover'
import LogisticsCreationListMenu from '../SitemapCards/LogisticsCreationListMenu'
import PinchZoomPan from './PinchZoomPan'
import { reorderById } from './reorderById'

import './MapViewsGallery.scss'

// Must outlast the cue animation, or the wash is stripped mid-fade and the switch reads as a blink.
const SWITCH_CUE_MS = 2000

const MIN_SCALE = 1
const MAX_SCALE = 8

// Pill geometry (px). The map controls anchor one gap below the pill on mobile.
const PILL_TOP = 8
const PILL_TOP_WITH_FILTERS = 126 // clears the mobile Logistics filters bar
const PILL_HEIGHT = 48
const MAP_CONTROLS_TOP_GAP = 8

const noop = (): void => undefined

export interface IMapConfiguration {
  globeViewId?: string
  sitemapId?: string
}

interface IProps {
  eventName?: EventTypes
  eventsStore?: EventsStore

  selectGlobe?: (globe: GlobeView) => void
  mapIds: IMapConfiguration[]
  globeViewsStore?: GlobeViewsStore
  selectedGlobeViewId?: string
  selectSitemap?: (sitemap: Sitemap) => void
  selectedWhiteboardId?: string
  sitemapsStore?: SitemapsStore
  openFormTypeSelectionForm?: () => void
  // Receives the gallery root ref so the map's Fullscreen control can target
  // the whole gallery (pill + sidebar + overlays), not just the map canvas.
  renderMapViewComponent?: (
    fullscreenTargetRef?: React.RefObject<HTMLDivElement>,
  ) => JSX.Element

  /** Bumped whenever the workflow focus seam retargets the map view; the user's own picks never bump it. */
  systemMapSwitchNonce?: number
  /** Offered while a map reached through a link is on screen: the way back to where it came from. */
  mapReturn?: { name: string; back: () => void }
  shouldDisableZoom?: boolean
  shouldShowCreateNewButton?: boolean
  mapActions?: React.ReactNode
  shouldUseFullHeight?: boolean
  mapBoxViewerStore?: MapBoxViewerStore

  hasFiltersBar?: boolean
  canReorder?: boolean

  isMobileEnv?: boolean
  locationAttributesStore?: LocationAttributesStore

  /** When true, hides the select-menu and auto-closes nested popups on next update. */
  isCollapsed?: boolean
  /** Mobile workflow-creation mode: hide the view picker and lift the map controls
   *  to the top so the map + pin get maximum room above the bottom-sheet form. */
  isCreatingWorkflow?: boolean
}

@inject(
  'sitemapsStore',
  'globeViewsStore',
  'eventsStore',
  'isMobileEnv',
  'locationAttributesStore',
)
@observer
export default class MapViewsGallery extends React.Component<IProps> {
  public static defaultProps = {
    canReorder: true,
  }

  @observable public isSelectMenuShown: boolean = false
  @observable private switchCue: { key: number; name: string } = null
  private switchCueTimer: number
  // Fullscreen target: covers the pill/sidebar + map + markup overlays.
  private readonly rootRef = React.createRef<HTMLDivElement>()
  private readonly pillRef = React.createRef<HTMLDivElement>()

  // Observable read (this component is an observer) — one shared tracker app-wide.
  private get isFullscreen(): boolean {
    return !!getFullscreenElement()
  }

  public constructor(props: IProps) {
    super(props)
    makeObservable(this)
  }

  // In fullscreen, the map list popover must portal INTO the fullscreen element
  // (the top layer) — a body portal wouldn't render. Undefined otherwise so
  // base-ui keeps its default body portal (null would disable the portal).
  private get sidebarPortalContainer(): HTMLElement | undefined {
    return (getFullscreenElement() as HTMLElement) ?? undefined
  }

  // Side-effect: parent flipping isCollapsed hides the open sub-menu.
  @action
  public componentDidUpdate(prevProps: IProps): void {
    if (this.props.isCollapsed && this.isSelectMenuShown) {
      this.isSelectMenuShown = false
    }
    this.syncSwitchCue(prevProps)
  }

  /**
   * Cue a map view the user did not pick. componentDidUpdate rather than a reaction: mobx-react
   * throws on reading this.props inside a foreign derivation, and both props land in one commit.
   */
  @action
  private syncSwitchCue(prevProps: IProps): void {
    const { systemMapSwitchNonce, selectedGlobeViewId } = this.props

    if (systemMapSwitchNonce !== prevProps.systemMapSwitchNonce) {
      this.switchCue = {
        key: systemMapSwitchNonce,
        name: this.selectedGlobe?.name || DEFAULT_NAME,
      }
      clearTimeout(this.switchCueTimer)
      this.switchCueTimer = window.setTimeout(
        this.clearSwitchCue,
        SWITCH_CUE_MS,
      )
      return
    }

    // The user has since picked a map themselves; leaving the cue up would announce their own choice.
    if (
      this.switchCue &&
      selectedGlobeViewId !== prevProps.selectedGlobeViewId
    ) {
      this.clearSwitchCue()
    }
  }

  @action.bound
  private clearSwitchCue(): void {
    clearTimeout(this.switchCueTimer)
    this.switchCue = null
  }

  public componentWillUnmount(): void {
    clearTimeout(this.switchCueTimer)
    this.props.mapBoxViewerStore?.resetMapboxInfo()
  }

  // Plain getters: mobx-react forbids reading this.props from a @computed reaction.
  private get currentMapIndex(): number | null {
    const index = this.mapIds.findIndex(
      mapId =>
        (mapId.globeViewId &&
          mapId.globeViewId === this.props.selectedGlobeViewId) ||
        (mapId.sitemapId &&
          mapId.sitemapId === this.props.selectedWhiteboardId),
    )
    return index === -1 ? null : index
  }

  private get selectedGlobe(): GlobeView | null {
    const { selectedGlobeViewId, globeViewsStore } = this.props
    return selectedGlobeViewId
      ? globeViewsStore.byId.get(selectedGlobeViewId)
      : null
  }

  private get selectedSitemap(): Sitemap | null {
    const { selectedWhiteboardId, sitemapsStore } = this.props
    return selectedWhiteboardId
      ? sitemapsStore.byId.get(selectedWhiteboardId)
      : null
  }

  private get subtitle(): string {
    return buildMapViewSubtitle({
      globe: this.selectedGlobe,
      sitemap: this.selectedSitemap,
      locationAttributesStore: this.props.locationAttributesStore,
    })
  }

  private get mapIds(): IMapConfiguration[] {
    return this.props.mapIds || []
  }

  // Adapter feeding the MapViewsSidebar list.
  private get mapItems(): IMapViewItem[] {
    const { globeViewsStore, sitemapsStore, locationAttributesStore } =
      this.props
    const sources: MapViewSource[] = []

    for (const { globeViewId, sitemapId } of this.mapIds) {
      const globe = globeViewId
        ? globeViewsStore.byId.get(globeViewId)
        : undefined
      const sitemap = sitemapId ? sitemapsStore.byId.get(sitemapId) : undefined
      if (globe) sources.push({ kind: 'globe', map: globe })
      else if (sitemap) sources.push({ kind: 'sitemap', map: sitemap })
    }

    return buildMapViewItems({ sources, locationAttributesStore })
  }

  // Globe and Sitemap ids share one id-space; pass whichever is active.
  private get selectedMapViewId(): string | undefined {
    return this.props.selectedGlobeViewId || this.props.selectedWhiteboardId
  }

  // Admins only, and only when an eventName is supplied to dispatch into.
  private get canReorderMaps(): boolean {
    const { canReorder, eventName, eventsStore } = this.props
    if (canReorder === false || !eventName) return false
    return !!eventsStore?.appState?.userActiveProjectSettings?.isAdmin
  }

  private get pillTop(): number {
    return this.props.hasFiltersBar ? PILL_TOP_WITH_FILTERS : PILL_TOP
  }

  // Published as --map-controls-top so mobile map controls clear the pill. During
  // workflow creation the filters bar + pill are hidden, so the controls rise to
  // the top edge to free the map.
  private get controlsTopOffset(): number {
    if (this.props.isCreatingWorkflow) {
      return PILL_TOP + MAP_CONTROLS_TOP_GAP
    }
    return this.pillTop + PILL_HEIGHT + MAP_CONTROLS_TOP_GAP
  }

  public render() {
    const {
      shouldUseFullHeight,
      shouldShowCreateNewButton,
      openFormTypeSelectionForm,
      mapActions,
    } = this.props

    return (
      <div
        ref={this.rootRef}
        className={classList({
          'sitemaps relative': true,
          'full-height': shouldUseFullHeight,
        })}
      >
        <div
          className={classList({
            'sitemaps-gallery absolute full-height full-width': true,
            'select-menu-opened': this.isSelectMenuShown,
          })}
          style={
            {
              '--map-controls-top': `${this.controlsTopOffset}px`,
            } as React.CSSProperties
          }
        >
          {this.renderPillWithSidebar()}

          {!this.isFullscreen && (mapActions || shouldShowCreateNewButton) && (
            <div className="tw:absolute tw:top-2.5 tw:right-2.5 tw:z-12 tw:flex tw:items-center tw:gap-2">
              {mapActions}
              {shouldShowCreateNewButton && (
                <LogisticsCreationListMenu
                  openFormTypeSelectionForm={openFormTypeSelectionForm}
                />
              )}
            </div>
          )}

          {this.renderItem()}
        </div>
      </div>
    )
  }

  // The title change alone is silent to a screen reader — only the "N of M" counter is announced.
  private renderSwitchAnnouncement() {
    return (
      <span className="tw:sr-only" aria-live="polite">
        {this.switchCue
          ? Localization.translator.switchedToMapViewX(this.switchCue.name)
          : ''}
      </span>
    )
  }

  private renderBack(isStyleLocked: boolean) {
    const { mapReturn } = this.props
    return (
      mapReturn && (
        <MapViewsPicker.Back
          label={Localization.translator.backToX(mapReturn.name)}
          onClick={mapReturn.back}
          isDisabled={isStyleLocked}
        />
      )
    )
  }

  // Desktop: Popover.Trigger owns toggle; Mobile: bottom-sheet drawer.
  private renderPillWithSidebar() {
    const { selectedGlobeViewId, isCollapsed, mapBoxViewerStore, isMobileEnv } =
      this.props
    if (isCollapsed || this.props.isCreatingWorkflow) {
      return null
    }

    const title =
      this.selectedGlobe?.name || this.selectedSitemap?.name || DEFAULT_NAME
    const total = this.mapIds.length
    const currentIndex = (this.currentMapIndex ?? -1) + 1

    const isStyleLocked =
      !!selectedGlobeViewId && !mapBoxViewerStore?.isStyleLoaded

    const pillClasses = classList({
      'tw:absolute tw:left-2 tw:right-2 tw:z-[5] tw:flex': true,
      // Figma caps: 361 desktop, 400 mobile.
      'tw:max-w-[361px]': !isMobileEnv,
      'tw:max-w-[400px]': isMobileEnv,
    })

    const renderPagination = () => (
      <MapViewsPicker.Pagination
        currentIndex={currentIndex}
        total={total}
        onPrev={this.selectPrevMap}
        onNext={this.selectNextMap}
        isPrevDisabled={isStyleLocked}
        isNextDisabled={isStyleLocked}
        switchCueKey={this.switchCue?.key}
      />
    )

    if (isMobileEnv) {
      const togglePill = () => this.setSelectMenuShown(!this.isSelectMenuShown)
      return (
        <>
          <div className={pillClasses} style={{ top: this.pillTop }}>
            {this.renderSwitchAnnouncement()}
            <MapViewsPicker.Root>
              {this.renderBack(isStyleLocked)}
              <MapViewsPicker.Trigger
                title={title}
                subtitle={this.subtitle}
                isOpen={this.isSelectMenuShown}
                onClick={togglePill}
                switchCueKey={this.switchCue?.key}
                hideIcon={!!this.props.mapReturn}
              />
              {renderPagination()}
            </MapViewsPicker.Root>
          </div>
          <MapViewsSidebar
            surface="drawer"
            open={this.isSelectMenuShown}
            onOpenChange={this.setSelectMenuShown}
            items={this.mapItems}
            selectedId={this.selectedMapViewId}
            onSelect={this.handleSelectMapView}
            canEdit={this.canReorderMaps}
            onReorder={this.canReorderMaps ? this.handleReorderMap : undefined}
          />
        </>
      )
    }

    const pill = (
      <div
        ref={this.pillRef}
        className={pillClasses}
        style={{ top: this.pillTop }}
      >
        {this.renderSwitchAnnouncement()}
        <MapViewsPicker.Root>
          {this.renderBack(isStyleLocked)}
          {/* Popover.Trigger owns open/close; isInteractive keeps chrome lit. */}
          <PopoverTrigger
            render={
              <MapViewsPicker.Trigger
                title={title}
                subtitle={this.subtitle}
                isOpen={this.isSelectMenuShown}
                isInteractive
                switchCueKey={this.switchCue?.key}
                hideIcon={!!this.props.mapReturn}
              />
            }
          />
          {renderPagination()}
        </MapViewsPicker.Root>
      </div>
    )

    return (
      <MapViewsSidebar
        surface="popover"
        open={this.isSelectMenuShown}
        onOpenChange={this.setSelectMenuShown}
        items={this.mapItems}
        selectedId={this.selectedMapViewId}
        onSelect={this.handleSelectMapView}
        canEdit={this.canReorderMaps}
        onReorder={this.canReorderMaps ? this.handleReorderMap : undefined}
        trigger={pill}
        anchor={this.pillRef}
        portalContainer={this.sidebarPortalContainer}
      />
    )
  }

  @action.bound
  private setSelectMenuShown(open: boolean) {
    this.isSelectMenuShown = open
  }

  // List-item picks dismiss the sidebar; pagination arrows do not.
  @action.bound
  private handleSelectMapView(id: string) {
    const { globeViewsStore, sitemapsStore } = this.props
    const globe = globeViewsStore.byId.get(id)
    if (globe) {
      this.selectGlobe(globe)
      this.isSelectMenuShown = false
      return
    }
    const sitemap = sitemapsStore.byId.get(id)
    if (sitemap) {
      this.selectWhiteboard(sitemap)
      this.isSelectMenuShown = false
    }
  }

  // Navigate-only; closing the sidebar is the call site's responsibility.
  @action
  private selectGlobe = (globe: GlobeView) => {
    this.selectGlobeImageById(globe.id)
  }

  @action
  private selectWhiteboard = (whiteboard: Sitemap) => {
    this.selectWhiteboardImageById(whiteboard.id)
  }

  private selectGlobeImageById = (id: string) => {
    const index = this.mapIds.findIndex(mapId => mapId.globeViewId === id)
    this.onSlide(index)
  }

  private selectWhiteboardImageById = (id: string) => {
    const index = this.mapIds.findIndex(mapId => mapId.sitemapId === id)
    this.onSlide(index)
  }

  private onSlide = (currentIndex: number) => {
    const { selectSitemap, selectGlobe, sitemapsStore, globeViewsStore } =
      this.props
    if (this.currentMapIndex !== currentIndex) {
      const mapId = this.getMapIdByIndex(currentIndex)

      const globe = globeViewsStore.byId.get(mapId?.globeViewId)
      if (globe) {
        selectGlobe(globe)
      }
      const sitemap = sitemapsStore.byId.get(mapId?.sitemapId)
      if (sitemap) {
        selectSitemap(sitemap)
      }
    }
  }

  private getMapIdByIndex(index: number): IMapConfiguration {
    return this.mapIds[index]
  }

  private renderItem = () => {
    if (this.props.shouldDisableZoom) {
      return this.renderImage()
    }

    return (
      <PinchZoomPan
        maxScale={MAX_SCALE}
        minScale={MIN_SCALE}
        zoomButtons={false}
        position="center"
        doubleTapBehavior="zoom"
        onScaleChanged={noop}
      >
        {this.renderImage()}
      </PinchZoomPan>
    )
  }

  private renderImage = () => {
    const { renderMapViewComponent } = this.props

    if (renderMapViewComponent) {
      return (
        <div className="full-width full-height selected-sitemap">
          {renderMapViewComponent(this.rootRef)}
        </div>
      )
    }

    return <div />
  }

  // Pagination spans the full mapIds list; current-index null = selected map dropped out, snap to 0.
  private selectNextMap = () => {
    const { mapBoxViewerStore, selectedGlobeViewId } = this.props
    if (selectedGlobeViewId && !mapBoxViewerStore?.isStyleLoaded) return
    const len = this.mapIds.length
    if (!len) return
    const current = this.currentMapIndex
    if (current === null) {
      this.onSlide(0)
      return
    }
    this.onSlide(current === len - 1 ? 0 : current + 1)
  }

  private selectPrevMap = () => {
    const { mapBoxViewerStore, selectedGlobeViewId } = this.props
    if (selectedGlobeViewId && !mapBoxViewerStore?.isStyleLoaded) return
    const len = this.mapIds.length
    if (!len) return
    const current = this.currentMapIndex
    if (current === null) {
      this.onSlide(0)
      return
    }
    this.onSlide(current === 0 ? len - 1 : current - 1)
  }

  // Reorder operates on stable card ids resolved against full mapIds (so search-filtered drags still target persisted state).
  @action.bound
  private handleReorderMap(
    fromId: string,
    toId: string,
    position: 'before' | 'after',
  ): void {
    const { eventsStore, eventName } = this.props
    if (!eventName) return
    const idOf = ({ globeViewId, sitemapId }: IMapConfiguration): string =>
      globeViewId || sitemapId
    const reordered = reorderById(this.mapIds, idOf, fromId, toId, position)
    if (!reordered) return
    const orderedMaps = reordered.map(({ sitemapId, globeViewId }, index) => ({
      sitemapId,
      globeViewId,
      order: index,
    }))
    eventsStore?.dispatch(eventName, { maps: orderedMaps })
  }
}
